/**
 * Data Grid Component
 * Reusable sortable/filterable table component.
 *
 * @typedef {Object} GridColumn
 * @property {string} key       - Data field name
 * @property {string} label     - Column header text
 * @property {Function} [format] - Optional formatter (value, row) → string
 * @property {boolean} [sortable] - Default true
 * @property {string} [align]   - 'left' | 'right' | 'center'
 */

/**
 * @typedef {Object} GridConfig
 * @property {GridColumn[]} columns
 * @property {Object[]} data
 * @property {string} [defaultSort]    - Column key for initial sort
 * @property {string} [defaultOrder]   - 'asc' | 'desc'
 * @property {string} [emptyMessage]   - Shown when data is empty
 * @property {boolean} [filterable]    - Show filter input, default true
 * @property {Function} [onRowClick]   - (row) => void — called when a data row is clicked
 * @property {Function} [onAdd]        - () => void — if provided, renders "Add" button above grid
 * @property {string} [addLabel]       - Label for add button (default "Add")
 * @property {Function} [rowClass]     - (row) => string|null — CSS class for the row
 * @property {string} [heading]        - Optional section heading text displayed left in the header row
 */

/**
 * createDataGrid(config: GridConfig) → HTMLElement
 *
 * Returns a <div> containing:
 * - Filter input (if filterable)
 * - Row count label: "Showing X of Y"
 * - <table> with sortable headers and data rows
 * - Empty-state message when no rows match
 */
export function createDataGrid(config) {
  const {
    columns,
    data,
    defaultSort = null,
    defaultOrder = 'asc',
    emptyMessage = 'No records found',
    filterable = true,
    onRowClick = null,
    onAdd = null,
    addLabel = 'Add',
    rowClass = null,
    heading = null,
  } = config;

  // State
  let sortKey = defaultSort;
  let sortOrder = defaultOrder;
  let filterText = '';
  let debounceTimer = null;
  let currentPage = 0;
  let pageSize = 10;

  // Root container
  const container = document.createElement('div');
  container.className = 'data-grid';

  // Header row — flex container with heading (left) and filter (right)
  const headerRow = document.createElement('div');
  headerRow.className = 'data-grid__header-row';

  if (heading) {
    const headingEl = document.createElement('h2');
    headingEl.className = 'data-grid__heading';
    headingEl.textContent = heading;
    headerRow.appendChild(headingEl);
  }

  const headerRight = document.createElement('div');
  headerRight.className = 'data-grid__header-right';

  // Filter input
  let filterInput = null;
  if (filterable) {
    filterInput = document.createElement('input');
    filterInput.type = 'text';
    filterInput.className = 'data-grid__filter';
    filterInput.placeholder = 'Filter…';
    filterInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        filterText = filterInput.value;
        currentPage = 0;
        render();
      }, 200);
    });
    headerRight.appendChild(filterInput);
  }

  headerRow.appendChild(headerRight);
  container.appendChild(headerRow);

  // Subheader row — add button + pagination area
  const subheader = document.createElement('div');
  subheader.className = 'data-grid__subheader';

  // Add button
  if (onAdd) {
    const addBtn = document.createElement('button');
    addBtn.className = 'btn btn--primary data-grid__add-btn';
    addBtn.textContent = addLabel;
    addBtn.addEventListener('click', () => onAdd());
    subheader.appendChild(addBtn);
  }

  // Pagination area (contains row count)
  const paginationArea = document.createElement('div');
  paginationArea.className = 'data-grid__pagination-area';

  // Row count label
  const rowCountLabel = document.createElement('div');
  rowCountLabel.className = 'data-grid__row-count';
  paginationArea.appendChild(rowCountLabel);

  // Page size selector
  const pageSizeSelect = document.createElement('select');
  pageSizeSelect.className = 'data-grid__page-size';
  [10, 25, 50, 100, 'All'].forEach((opt) => {
    const option = document.createElement('option');
    option.value = opt === 'All' ? 'Infinity' : String(opt);
    option.textContent = String(opt);
    pageSizeSelect.appendChild(option);
  });
  pageSizeSelect.addEventListener('change', () => {
    const val = pageSizeSelect.value;
    pageSize = val === 'Infinity' ? Infinity : Number(val);
    currentPage = 0;
    render();
  });
  paginationArea.appendChild(pageSizeSelect);

  // Pagination buttons container
  const paginationBtns = document.createElement('div');
  paginationBtns.className = 'data-grid__pagination-btns';

  const prevBtn = document.createElement('button');
  prevBtn.textContent = 'Prev';
  prevBtn.addEventListener('click', () => {
    if (currentPage > 0) {
      currentPage--;
      render();
    }
  });
  paginationBtns.appendChild(prevBtn);

  const nextBtn = document.createElement('button');
  nextBtn.textContent = 'Next';
  nextBtn.addEventListener('click', () => {
    currentPage++;
    render();
  });
  paginationBtns.appendChild(nextBtn);

  paginationArea.appendChild(paginationBtns);

  subheader.appendChild(paginationArea);
  container.appendChild(subheader);

  // Table
  const table = document.createElement('table');
  table.className = 'data-grid__table';
  container.appendChild(table);

  // Empty state
  const emptyState = document.createElement('div');
  emptyState.className = 'data-grid__empty';
  emptyState.textContent = emptyMessage;
  container.appendChild(emptyState);

  /**
   * Get the display value for a cell (applies formatter if present).
   */
  function getCellDisplay(col, row) {
    const value = row[col.key];
    if (col.format) {
      return col.format(value, row);
    }
    return value == null ? '' : String(value);
  }

  /**
   * Filter data based on current filterText.
   */
  function getFilteredData() {
    if (!filterText) return data;
    const lower = filterText.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => getCellDisplay(col, row).toLowerCase().includes(lower))
    );
  }

  /**
   * Sort an array of rows by the current sortKey/sortOrder.
   */
  function getSortedData(rows) {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return rows;

    return [...rows].sort((a, b) => {
      const aRaw = a[col.key];
      const bRaw = b[col.key];
      // Use raw values for comparison so numeric fields sort correctly
      const aVal = (typeof aRaw === 'number' || typeof bRaw === 'number')
        ? aRaw : (col.format ? col.format(aRaw, a) : aRaw);
      const bVal = (typeof aRaw === 'number' || typeof bRaw === 'number')
        ? bRaw : (col.format ? col.format(bRaw, b) : bRaw);

      let cmp = 0;
      if (aVal == null && bVal == null) cmp = 0;
      else if (aVal == null) cmp = -1;
      else if (bVal == null) cmp = 1;
      else if (typeof aVal === 'number' && typeof bVal === 'number') cmp = aVal - bVal;
      else cmp = String(aVal).localeCompare(String(bVal));

      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  /**
   * Render the table with current sort/filter state.
   */
  function render() {
    const filtered = getFilteredData();
    const sorted = getSortedData(filtered);
    const totalFiltered = sorted.length;

    // Pagination slice
    const showAll = pageSize === Infinity;
    const pageStart = showAll ? 0 : currentPage * pageSize;
    const pageEnd = showAll ? totalFiltered : Math.min(pageStart + pageSize, totalFiltered);
    const pageData = sorted.slice(pageStart, pageEnd);

    // Update row count
    if (totalFiltered === 0) {
      rowCountLabel.textContent = 'Showing 0 of 0';
    } else {
      rowCountLabel.textContent = `Showing ${pageStart + 1}\u2013${pageEnd} of ${totalFiltered}`;
    }

    // Build thead
    const thead = document.createElement('thead');
    const tableHeaderRow = document.createElement('tr');
    columns.forEach((col) => {
      const th = document.createElement('th');
      th.className = 'data-grid__header';
      if (col.className) th.classList.add(col.className);
      const isSortable = col.sortable !== false;

      let headerText = col.label;
      if (isSortable && sortKey === col.key) {
        headerText += sortOrder === 'asc' ? ' ▲' : ' ▼';
      }
      th.textContent = headerText;

      if (col.align) {
        th.style.textAlign = col.align;
      }

      if (isSortable) {
        th.style.cursor = 'pointer';
        th.addEventListener('click', () => {
          if (sortKey === col.key) {
            sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
          } else {
            sortKey = col.key;
            sortOrder = 'asc';
          }
          render();
        });
      }

      tableHeaderRow.appendChild(th);
    });

    thead.appendChild(tableHeaderRow);

    // Build tbody
    const tbody = document.createElement('tbody');
    pageData.forEach((row) => {
      const tr = document.createElement('tr');
      tr.className = 'data-grid__row';

      // Apply conditional row class
      if (rowClass) {
        const cls = rowClass(row);
        if (cls) {
          tr.classList.add(cls);
        }
      }

      // Row click handler
      if (onRowClick) {
        tr.style.cursor = 'pointer';
        tr.addEventListener('click', (e) => {
          // Don't trigger row click when clicking links or checkboxes
          if (e.target.closest('a') || e.target.closest('input[type="checkbox"]')) return;
          onRowClick(row);
        });
      }

      columns.forEach((col) => {
        const td = document.createElement('td');
        td.className = 'data-grid__cell';
        if (col.className) td.classList.add(col.className);
        if (col.html) {
          td.innerHTML = getCellDisplay(col, row);
        } else {
          td.textContent = getCellDisplay(col, row);
        }
        if (col.align) {
          td.style.textAlign = col.align;
        }
        tr.appendChild(td);
      });

      tbody.appendChild(tr);
    });

    // Update pagination button visibility and disabled states
    const hasPagination = totalFiltered > pageSize && !showAll;
    if (hasPagination) {
      paginationBtns.style.display = '';
      prevBtn.disabled = currentPage === 0;
      const lastPage = Math.ceil(totalFiltered / pageSize) - 1;
      nextBtn.disabled = currentPage >= lastPage;
    } else {
      paginationBtns.style.display = 'none';
      prevBtn.disabled = true;
      nextBtn.disabled = true;
    }

    // Replace table content
    table.innerHTML = '';
    table.appendChild(thead);
    table.appendChild(tbody);

    // Toggle empty state vs table visibility
    if (totalFiltered === 0) {
      table.style.display = 'none';
      emptyState.style.display = '';
    } else {
      table.style.display = '';
      emptyState.style.display = 'none';
    }
  }

  // Initial render
  render();

  return container;
}
